import { useState } from "react";
import CustomSelect from "../UI/CustomSelect";
import { CustomDatePicker } from "../Forms";
import { useTheme } from "@hooks/useTheme";
import { useTranslation } from "react-i18next";
import { CHARACTER_LENGTH } from "@constants/index";
import { OutlineButton, PrimaryButton } from "@components/Buttons";

const AddNewRequestModal = ({
  isOpen,
  onClose,
  onSubmit,
  assigneeOptions = [],
  systemMessage = null,
  "data-testid": dataTestId = "add-new-request-modal",
}) => {
  const fieldContainerTestId = (name) => `${dataTestId}-field-${name}-container`;
  const fieldLabelTestId = (name) => `${dataTestId}-field-${name}-label`;
  const fieldInputTestId = (name) => `${dataTestId}-field-${name}-input`;
  const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    assignee: "",
    completionRequestDate: null,
    requestMessage: ""
  });

  const isFormValid = formData.assignee && formData.completionRequestDate && formData.requestMessage.trim();

  const filteredAssigneeOptions = assigneeOptions.filter(option => {
    const loggedInUserId = localStorage.getItem("userId");
    return option.value !== loggedInUserId;
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!isFormValid) {
      console.error("Form validation failed");
      return;
    }

    const submitData = {
      ...formData,
      ...(systemMessage && {
        cve: systemMessage.cve,
        actionSeq: systemMessage.actionSeq
      })
    };

    if (onSubmit) {
      onSubmit(submitData);
    }

    setFormData({
      assignee: "",
      completionRequestDate: null,
      requestMessage: ""
    });
    onClose();
  };

  const handleClose = () => {
    setFormData({
      assignee: "",
      completionRequestDate: null,
      requestMessage: ""
    });
    onClose();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start md:justify-center backdrop-blur-[1px] overflow-x-auto p-4`}
      data-testid={`${dataTestId}-overlay`}
    >
      <div
        className={`border ${colors.borderPrimary} ${colors.bgModal} min-h-[58vh] max-h-[80vh] w-[800px] min-w-[800px] flex-shrink-0 flex flex-col rounded-lg shadow-xl`}
        data-testid={dataTestId}
      >
        {/* Header */}
        <div className="px-6 py-6 flex-shrink-0" data-testid={`${dataTestId}-header`}>
          <div className="flex items-center justify-between">
            <h2 className={`${colors.textPrimary} text-xl font-bold`} data-testid={`${dataTestId}-title`}>
              {t("communication.add-new-request-title")}
            </h2>
          </div>
        </div>

        {/* Content - Scrollable */}
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden" data-testid={`${dataTestId}-form`}>
          <div className="flex-1 overflow-y-auto px-6 space-y-4" data-testid={`${dataTestId}-content`}>
            <div className="flex gap-6">
              {/* Assignee */}
              <div className="flex-1" data-testid={fieldContainerTestId("assignee")}>
                <label
                  className={`mb-2 block text-sm font-medium ${colors.textPrimary}`}
                  data-testid={fieldLabelTestId("assignee")}
                >
                  {t("communication.assignee")} <span className={colors.textDanger}>*</span>
                </label>
                <CustomSelect
                  data-testid={fieldInputTestId("assignee")}
                  options={filteredAssigneeOptions}
                  value={filteredAssigneeOptions.find(opt => opt.value === formData.assignee) || null}
                  onChange={(opt) => handleInputChange("assignee", opt.value)}
                  placeholder={t("communication.select-assignee")}
                  className="w-full text-sm"
                  displayFormatLabelWithID={true}
                />
              </div>

              {/* Completion Request Date */}
              <div className="flex-1" data-testid={fieldContainerTestId("completion-request-date")}>
                <label
                  className={`mb-2 block text-sm font-medium ${colors.textPrimary}`}
                  data-testid={fieldLabelTestId("completion-request-date")}
                >
                  {t("communication.completion-request-date")} <span className={colors.textDanger}>*</span>
                </label>
                <div data-testid={fieldInputTestId("completion-request-date")}>
                  <CustomDatePicker
                    selected={formData.completionRequestDate}
                    onChange={(date) => handleInputChange("completionRequestDate", date)}
                    placeholderText={t("communication.select-date")}
                    className={`h-10 w-full appearance-none rounded-lg border p-3 text-sm font-medium focus:outline-none ${colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                    dateFormat="yyyy-MM-dd"
                    minDate={new Date()}
                  />
                </div>
              </div>
            </div>

            {/* Request Message */}
            <div data-testid={fieldContainerTestId("request-message")}>
              <label
                className={`mb-2 block text-sm font-medium ${colors.textPrimary}`}
                data-testid={fieldLabelTestId("request-message")}
              >
                {t("communication.request-message")} <span className={colors.textDanger}>*</span>
              </label>
              <textarea
                data-testid={fieldInputTestId("request-message")}
                value={formData.requestMessage}
                onChange={(e) => handleInputChange("requestMessage", e.target.value)}
                placeholder={t("communication.write-message")}
                maxLength={CHARACTER_LENGTH.REQUEST}
                className={`w-full rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} resize-none`}
                rows="11"
                required
              />
              <p className={`text-xs ${colors.textMuted} mt-1`} data-testid={fieldValueTestId("request-message-char-count")}>
                {formData.requestMessage.length}/{CHARACTER_LENGTH.REQUEST}
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 py-4" data-testid={`${dataTestId}-footer`}>
            <div className={`h-px w-full ${colors.bgDivider} mb-4`}></div>

            <div className={`${colors.borderPrimary} px-6`}>
              <div className="flex items-center justify-end">
                <div className="flex items-center space-x-3">
                  <OutlineButton data-testid={`${dataTestId}-cancel-button`} onClick={handleClose}>
                    {t("communication.cancel")}
                  </OutlineButton>
                  <PrimaryButton
                    data-testid={`${dataTestId}-confirm-button`}
                    onClick={handleSubmit}
                    disabled={!isFormValid}
                  >
                    {t("communication.send-request")}
                  </PrimaryButton>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddNewRequestModal;
